import React, { FC, ReactNode } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { POSITION_TYPE } from 'transfix-maps/types'
import { Button } from 'transfix-ui/components/Button'
import { RelativeDateTime } from 'transfix-ui/components/RelativeDateTime'
import { TAB } from 'apps/fleet-planner/constants/loads'
import { ILocationELD, IShipmentETA } from 'apps/fleet-planner/types'
import { setActiveTab } from 'apps/fleet-planner/utils/actions/loads'
import { SubtitleComponentDispatch } from './SubtitleComponentDispatch'
import { SubtitleComponentLocation } from './SubtitleComponentLocation'

export interface ISubtitleComponentProps {
  onClick?: () => void
  recentLocations?: ILocationELD[]
}

export interface ITitleComponentProps {
  eta?: IShipmentETA
}

export interface IFMSELDState {
  TitleComponent: FC<ITitleComponentProps>
  SubtitleComponent: FC<ISubtitleComponentProps>
  alertType?: string
  pingType?: POSITION_TYPE
}

export interface IFMSELDError {
  message?: ReactNode
}

export const FMS_ELD_STATES: Record<string, IFMSELDState> = {
  setUpToTrack: {
    TitleComponent: () => <span>Your load is set up for tracking</span>,
    SubtitleComponent: SubtitleComponentDispatch,
    alertType: 'default',
  },
  notSetUpToTrack: {
    TitleComponent: () => <span> Location unavailable</span>,
    SubtitleComponent: () => <span>Your carrier is not set up to track.</span>,
    alertType: 'error',
  },
  notSetUpToTrackMobile: {
    TitleComponent: () => <span>Location unavailable</span>,
    SubtitleComponent: () => (
      <span>Ask your driver to enable location sharing on the Transfix app.</span>
    ),
    alertType: 'error',
  },
  deliveringAnotherLoad: {
    TitleComponent: () => <span>Your driver is delivering another load</span>,
    SubtitleComponent: () => {
      const history = useHistory()
      const dispatch = useDispatch()

      /**
       * Redirects to the Loads page with Active Loads tab open
       */
      const redirectToActiveLoads = () => {
        dispatch(setActiveTab(TAB.ACTIVE))
        history.push('/loads')
      }

      return (
        <span>
          Your truck is assigned to other active loads.
          <Button className='btn-text-link-b' onClick={redirectToActiveLoads}>
            View Active Loads
          </Button>
        </span>
      )
    },
    alertType: 'default',
  },
  inTransitOnTime: {
    TitleComponent: ({ eta }) => {
      const { distance, eta: etaProp } = eta || {}
      const timeToDestination = etaProp ? (
        <RelativeDateTime datetime={etaProp} isShorthand inSentence={false} />
      ) : (
        'Unknown time'
      )
      return (
        <span>
          <strong>On Time</strong> {timeToDestination} to next stop ({Math.round(distance || 0)} mi)
        </span>
      )
    },
    SubtitleComponent: SubtitleComponentLocation,
    alertType: 'info',
  },
  inTransitLate: {
    TitleComponent: ({ eta }) => {
      const { distance, eta: etaProp } = eta || {}
      const timeToDestination = etaProp ? (
        <RelativeDateTime datetime={etaProp} isShorthand inSentence={false} />
      ) : (
        'Unknown time'
      )
      return (
        <span>
          <strong>Late</strong> {timeToDestination} to next stop ({Math.round(distance || 0)} mi)
        </span>
      )
    },
    SubtitleComponent: SubtitleComponentLocation,
    alertType: 'warning',
  },
  atPickupDropoff: {
    TitleComponent: () => <span />,
    SubtitleComponent: SubtitleComponentLocation,
    alertType: 'info',
  },
  delivered: {
    TitleComponent: () => <span>This load was delivered</span>,
    SubtitleComponent: SubtitleComponentLocation,
    alertType: 'default',
    pingType: POSITION_TYPE.DELIVERED,
  },
  p44LocationNotAvailableYet: {
    TitleComponent: () => <span>Location not available yet</span>,
    SubtitleComponent: () => (
      <span>Your trucks location will be shown within 2 hours of the pickup</span>
    ),
    alertType: 'default',
  },
  noPings: {
    TitleComponent: () => <span>Not tracking</span>,
    SubtitleComponent: () => <span>No ping detected yet</span>,
    alertType: 'error',
  },
  trackingStopped: {
    TitleComponent: () => <span>Not tracking</span>,
    SubtitleComponent: ({ recentLocations }) => {
      return <SubtitleComponentLocation recentLocations={recentLocations} lastSeen />
    },
    alertType: 'error',
    pingType: POSITION_TYPE.STOPPED,
  },
}

export type TELDStateKey = keyof typeof FMS_ELD_STATES
